/**
 * Wires the global-header fullscreen-toggle button (#967) to the Fullscreen
 * API. `top.html` is a server-rendered Jinja partial, not part of the Preact
 * tree, so this follows the same plain-DOM-effect pattern `app.tsx` already
 * uses for that header's `routerLink` anchors: a query for the button, a
 * handful of addEventListener calls, and a returned cleanup — called from a
 * thin `useEffect` in `app.tsx`, not a mounted component.
 */

import {
    isFullscreenSupported,
    isFullscreenActive,
    requestFullscreen,
    exitFullscreen,
    onFullscreenChange,
} from '@brainfocus/web/utils/fullscreen';
import { flash } from '@brainfocus/web/flash';

const LABEL_NOT_FULLSCREEN = 'FULLSCREEN';
const CAPTION_NOT_FULLSCREEN = 'hide browser toolbar';
const LABEL_FULLSCREEN = 'EXIT FULLSCREEN';
const CAPTION_FULLSCREEN = 'show browser toolbar';

// Shown when the browser refuses the request (#1013 third review): Chrome may do so
// after the page opened a pop-up window while fullscreen. The browser's own
// fullscreen is not subject to the page's request, so point the user to it.
export const NOTICE_ENTER_REFUSED = 'The browser didn\'t allow fullscreen here. Use the browser\'s own full screen instead (⌃⌘F on Mac, F11 on Windows).';
export const NOTICE_EXIT_REFUSED = 'The browser didn\'t leave fullscreen. Press Esc instead.';

/**
 * Cmd+Shift+F (macOS) or Ctrl+Shift+F (Windows/Linux) — either modifier is accepted.
 * Shared with detached windows, which capture it too (#1013, D16).
 */
export function isFullscreenShortcut(event: KeyboardEvent): boolean {
    return (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'f';
}

/**
 * Attach the fullscreen-toggle button's behavior. Returns a no-op cleanup if
 * the button isn't in the DOM or the Fullscreen API isn't supported (the
 * button is hidden in the latter case, per #967's acceptance criteria).
 */
export function wireFullscreenToggle(): () => void {
    const button = document.querySelector<HTMLButtonElement>('[data-testid="fullscreen-toggle"]');
    if (!button) {
        return () => {};
    }

    if (!isFullscreenSupported()) {
        button.hidden = true;
        return () => {};
    }

    // Markup ships `hidden` by default (progressive enhancement: a JS load
    // failure must never leave a dead button visible) — un-hide it now that
    // we know the API is actually supported.
    button.hidden = false;

    const label = button.querySelector<HTMLElement>('[data-testid="fullscreen-toggle-label"]');
    const caption = button.querySelector<HTMLElement>('[data-testid="fullscreen-toggle-caption"]');

    function sync(): void {
        const active = isFullscreenActive();
        button!.setAttribute('aria-pressed', String(active));
        button!.dataset['state'] = active ? 'fullscreen' : 'normal';
        if (label) label.textContent = active ? LABEL_FULLSCREEN : LABEL_NOT_FULLSCREEN;
        if (caption) caption.textContent = active ? CAPTION_FULLSCREEN : CAPTION_NOT_FULLSCREEN;
    }

    function toggle(): void {
        // A request/exit can be rejected outside the caller's control (e.g.
        // Chrome's "API can only be initiated by a user gesture" check, or its
        // refusal after the page opened a pop-up while fullscreen, #1013). Never an
        // unhandled rejection (PR #986 review), and never a silent dead click
        // either: resync and say so. Resyncing once settled also covers a
        // `fullscreenchange` the browser delivers late.
        const exiting = isFullscreenActive();
        const pending = exiting ? exitFullscreen() : requestFullscreen(document.documentElement);
        pending.then(sync, () => {
            sync();
            flash(exiting ? NOTICE_EXIT_REFUSED : NOTICE_ENTER_REFUSED);
        });
    }

    function onClick(): void {
        toggle();
    }

    function onKeydown(event: KeyboardEvent): void {
        if (!isFullscreenShortcut(event)) return;
        event.preventDefault();
        toggle();
    }

    button.addEventListener('click', onClick);
    window.addEventListener('keydown', onKeydown);
    const unsubscribeFullscreenChange = onFullscreenChange(sync);
    // `fullscreenchange` can arrive late, e.g. when Chrome leaves fullscreen
    // because the page opened a pop-up window (#1013): re-derive whenever the
    // window is back in front, so the label never claims a fullscreen that ended.
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);

    sync();

    return () => {
        button.removeEventListener('click', onClick);
        window.removeEventListener('keydown', onKeydown);
        unsubscribeFullscreenChange();
        window.removeEventListener('focus', sync);
        document.removeEventListener('visibilitychange', sync);
    };
}
